import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Calendar, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useServiceContent } from '../hooks/use-service-content';
import { useSiteSettings } from '../hooks/use-site-settings';
import { useSectionsContent } from '../hooks/use-sections-content';
import { getServiceSlug } from '../lib/service-utils';
import { useLanguage } from '../i18n/LanguageContext';

const submitAppointmentRequest = async (formData) => {
  const response = await fetch('/api/appointments', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.detail || 'Failed to submit appointment request');
  }

  return response.json();
};

const createPaypalOrder = async (payload) => {
  const response = await fetch('/api/payments/paypal/create-order', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.detail || 'Failed to create PayPal order');
  }

  return response.json();
};

const capturePaypalOrder = async (orderID) => {
  const response = await fetch('/api/payments/paypal/capture-order', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ orderID }),
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.detail || 'Failed to capture PayPal payment');
  }

  return response.json();
};

const fetchPaymentQuote = async (serviceType) => {
  const response = await fetch(`/api/payments/quote?serviceType=${encodeURIComponent(serviceType)}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.detail || 'Failed to fetch payment quote');
  }

  return response.json();
};

const fetchServiceAvailability = async (serviceType, date) => {
  const response = await fetch(
    `/api/availability?serviceType=${encodeURIComponent(serviceType)}&date=${encodeURIComponent(date)}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    },
  );

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.detail || 'Failed to fetch availability');
  }

  return response.json();
};

const normalizeCurrency = (value, fallback = 'EUR') => {
  const cleaned = String(value || '')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3);

  return cleaned || fallback;
};

const resolveServiceTypeFromSlug = (serviceSlug, appointmentTypes) => {
  if (!serviceSlug || !Array.isArray(appointmentTypes)) {
    return '';
  }

  const direct = appointmentTypes.find((item) => String(item?.value || '') === serviceSlug);
  if (direct) {
    return direct.value;
  }

  const exactServiceSlugMatch = appointmentTypes.find((item) => {
    const linkedServiceSlug = String(item?.serviceSlug || '').trim();
    if (!linkedServiceSlug) {
      return false;
    }
    return linkedServiceSlug === serviceSlug;
  });
  if (exactServiceSlugMatch) {
    return exactServiceSlugMatch.value;
  }

  const keyword = serviceSlug.includes('massage')
    ? 'massage'
    : serviceSlug.includes('workshop')
      ? 'workshop'
      : serviceSlug.includes('coaching')
        ? 'coaching'
        : serviceSlug.includes('consult')
          ? 'consult'
          : '';

  if (!keyword) {
    return '';
  }

  const fuzzy = appointmentTypes.find((item) =>
    String(item?.value || '').toLowerCase().includes(keyword),
  );
  return fuzzy?.value || '';
};

const loadPayPalSdk = ({ clientId, currency }) => {
  const src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=${encodeURIComponent(currency)}&intent=capture`;

  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Browser environment is required for PayPal'));
      return;
    }

    const existing = document.getElementById('paypal-sdk-script');
    if (existing && existing.getAttribute('src') === src && window.paypal?.Buttons) {
      resolve(window.paypal);
      return;
    }

    if (existing) {
      existing.remove();
      if (window.paypal) {
        delete window.paypal;
      }
    }

    const script = document.createElement('script');
    script.id = 'paypal-sdk-script';
    script.src = src;
    script.async = true;
    script.onload = () => resolve(window.paypal);
    script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
    document.body.appendChild(script);
  });
};

const formatMoney = (amount, currency) => {
  const numeric = Number(amount || 0);
  const normalizedCurrency = normalizeCurrency(currency, 'EUR');

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch (error) {
    return `${numeric.toFixed(2)} ${normalizedCurrency}`;
  }
};

const normalizePaymentOption = (value) => (String(value || '').toLowerCase() === 'full' ? 'full' : 'minimum');

const resolveDueNowAmount = (quote, paymentOption) => {
  if (!quote) {
    return null;
  }

  const normalizedOption = normalizePaymentOption(paymentOption);
  if (normalizedOption === 'full') {
    return Number(quote.totalAmount ?? 0);
  }

  return Number(quote.minimumBookingAmount ?? quote.totalAmount ?? 0);
};

const HOURLY_PREFERRED_TIMES = Array.from({ length: 16 }, (_, index) => {
  const hour = index + 6;
  const slot = `${String(hour).padStart(2, '0')}:00`;
  return {
    value: slot,
    label: slot,
  };
});

const normalizePreferredHour = (value) => {
  const match = String(value || '').trim().match(/^(\d{1,2})(?::\d{1,2})?$/);
  if (!match) {
    return '';
  }

  const hour = Number(match[1]);
  if (Number.isNaN(hour) || hour < 0 || hour > 23) {
    return '';
  }

  return `${String(hour).padStart(2, '0')}:00`;
};

const normalizeTimeSlots = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  const unique = [];
  values.forEach((value) => {
    const normalized = normalizePreferredHour(value);
    if (normalized && !unique.includes(normalized)) {
      unique.push(normalized);
    }
  });
  return unique.sort();
};

const createInitialFormData = ({ paymentMethod = 'manual', paymentCurrency = 'EUR' } = {}) => ({
  name: '',
  email: '',
  phone: '',
  serviceType: '',
  preferredDate: '',
  preferredTime: '',
  consultationType: '',
  message: '',
  paymentMethod,
  paymentOption: paymentMethod === 'paypal' ? 'minimum' : 'manual',
  paymentStatus: paymentMethod === 'manual' ? 'pending_manual' : 'pending',
  paymentAmount: null,
  paymentCurrency,
  paypalOrderId: '',
  paypalCaptureId: '',
  baseAmount: null,
  taxPercent: null,
  taxAmount: null,
  bookingFeeAmount: null,
  minimumBookingPercent: null,
  minimumBookingAmount: null,
  totalAmount: null,
  outstandingAmount: null,
});

const Booking = () => {
  const { toast } = useToast();
  const location = useLocation();
  const { services, appointmentTypes } = useServiceContent();
  const { siteSettings } = useSiteSettings();
  const { sectionsContent } = useSectionsContent();
  const { tr, localizeText } = useLanguage();
  const copy = sectionsContent?.publicPageCopy || {};
  const text = (key, fallback) => copy[key] || fallback;
  const activeServiceSlugs = useMemo(
    () =>
      Array.isArray(services)
        ? services
            .filter((service) => service?.isActive !== false)
            .map((service) => String(service?.slug || ''))
            .filter(Boolean)
        : [],
    [services],
  );
  const bookableAppointmentTypes = useMemo(
    () =>
      Array.isArray(appointmentTypes)
        ? appointmentTypes.filter((item) => {
            if (item?.isActive === false) {
              return false;
            }
            const linkedSlug = String(item?.serviceSlug || '').trim();
            if (!linkedSlug) {
              return true;
            }
            return activeServiceSlugs.includes(linkedSlug);
          })
        : [],
    [appointmentTypes, activeServiceSlugs],
  );

  const availablePaymentMethods = useMemo(() => {
    const methods = [];
    if (siteSettings.enableManualPayments) {
      methods.push('manual');
    }
    if (siteSettings.enablePaypalPayments) {
      methods.push('paypal');
    }
    return methods;
  }, [siteSettings.enableManualPayments, siteSettings.enablePaypalPayments]);

  const defaultCurrency = useMemo(
    () => normalizeCurrency(siteSettings.paypalCurrency, 'EUR'),
    [siteSettings.paypalCurrency],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPayPalScriptReady, setIsPayPalScriptReady] = useState(false);
  const [isPayPalProcessing, setIsPayPalProcessing] = useState(false);
  const [payPalError, setPayPalError] = useState('');
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [availability, setAvailability] = useState({
    availableTimeSlots: [],
    offDay: false,
    bufferMinutes: 0,
  });
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [pendingPayPalAppointment, setPendingPayPalAppointment] = useState(null);
  const [isRetryingPayPalConfirmation, setIsRetryingPayPalConfirmation] = useState(false);

  const [formData, setFormData] = useState(
    createInitialFormData({ paymentMethod: 'manual', paymentCurrency: defaultCurrency }),
  );

  const formDataRef = useRef(formData);
  const payPalButtonRef = useRef(null);
  const payPalOrderMetaRef = useRef(null);

  useEffect(() => {
    const reloadMarkerKey = 'booking_bundle_reload_once';
    const scriptSources = Array.from(document.querySelectorAll('script[src]'))
      .map((node) => String(node.getAttribute('src') || '').trim())
      .filter(Boolean);
    const currentBundleToken =
      scriptSources
        .map((src) => src.match(/main\.[a-f0-9]+\.js/i)?.[0] || '')
        .find(Boolean) || '';

    if (!currentBundleToken) {
      return undefined;
    }

    let isCancelled = false;

    fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((manifest) => {
        if (isCancelled || !manifest?.files?.['main.js']) {
          return;
        }

        const latestBundleToken = String(manifest.files['main.js']).match(/main\.[a-f0-9]+\.js/i)?.[0] || '';
        if (!latestBundleToken || latestBundleToken === currentBundleToken) {
          const existingMarker = sessionStorage.getItem(reloadMarkerKey);
          if (existingMarker && existingMarker === latestBundleToken) {
            sessionStorage.removeItem(reloadMarkerKey);
          }
          return;
        }

        const alreadyReloadedFor = sessionStorage.getItem(reloadMarkerKey);
        if (alreadyReloadedFor === latestBundleToken) {
          return;
        }

        sessionStorage.setItem(reloadMarkerKey, latestBundleToken);
        window.location.reload();
      })
      .catch(() => {
        // Ignore manifest check failures; checkout should continue as-is.
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (!availablePaymentMethods.length) {
      return;
    }

    setFormData((current) => {
      const currentMethod = availablePaymentMethods.includes(current.paymentMethod)
        ? current.paymentMethod
        : availablePaymentMethods[0];

      if (
        currentMethod === current.paymentMethod &&
        normalizeCurrency(current.paymentCurrency, defaultCurrency) === current.paymentCurrency
      ) {
        return current;
      }

      return {
        ...current,
        paymentMethod: currentMethod,
        paymentOption:
          currentMethod === 'paypal'
            ? normalizePaymentOption(current.paymentOption)
            : 'manual',
        paymentStatus: currentMethod === 'manual' ? 'pending_manual' : 'pending',
        paymentCurrency: normalizeCurrency(current.paymentCurrency, defaultCurrency),
      };
    });
  }, [availablePaymentMethods, defaultCurrency]);

  useEffect(() => {
    if (!bookableAppointmentTypes.length) {
      return;
    }

    const allowedValues = new Set(bookableAppointmentTypes.map((item) => String(item?.value || '')));
    setFormData((current) => {
      if (!current.serviceType || allowedValues.has(String(current.serviceType))) {
        return current;
      }

      return {
        ...current,
        serviceType: '',
        preferredDate: '',
        preferredTime: '',
      };
    });
  }, [bookableAppointmentTypes]);

  const appointmentTypeLabelsByValue = useMemo(
    () => ({
      'coaching-online': tr('appointmentType.coachingOnline'),
      'coaching-person': tr('appointmentType.coachingPerson'),
      massage: tr('appointmentType.massage'),
      workshop: tr('appointmentType.workshop'),
      consultation: tr('appointmentType.consultation'),
    }),
    [tr],
  );
  const selectedAppointmentType = useMemo(
    () =>
      bookableAppointmentTypes.find(
        (item) => String(item?.value || '') === String(formData.serviceType || ''),
      ) || null,
    [bookableAppointmentTypes, formData.serviceType],
  );
  const selectedAppointmentLabel = useMemo(() => {
    if (!selectedAppointmentType) {
      return '';
    }
    return (
      appointmentTypeLabelsByValue[selectedAppointmentType.value] ||
      localizeText(selectedAppointmentType.label)
    );
  }, [appointmentTypeLabelsByValue, localizeText, selectedAppointmentType]);
  const resolveServiceLabel = (serviceType) => {
    const matchedType = bookableAppointmentTypes.find(
      (item) => String(item?.value || '') === String(serviceType || ''),
    );
    if (!matchedType) {
      return String(serviceType || '-');
    }
    return appointmentTypeLabelsByValue[matchedType.value] || localizeText(matchedType.label);
  };
  const configuredTimeSlots = useMemo(
    () =>
      normalizeTimeSlots(
        selectedAppointmentType?.availableTimeSlots?.length
          ? selectedAppointmentType.availableTimeSlots
          : HOURLY_PREFERRED_TIMES.map((item) => item.value),
      ),
    [selectedAppointmentType],
  );
  const offDaysSet = useMemo(
    () =>
      new Set(
        Array.isArray(siteSettings.offDays)
          ? siteSettings.offDays.map((item) => String(item || '').trim())
          : [],
      ),
    [siteSettings.offDays],
  );
  const selectableTimeSlots = useMemo(() => {
    if (!formData.preferredDate) {
      return [];
    }
    if (!Array.isArray(availability.availableTimeSlots)) {
      return [];
    }
    return normalizeTimeSlots(availability.availableTimeSlots);
  }, [availability.availableTimeSlots, formData.preferredDate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedServiceSlug = String(params.get('service') || '').trim();
    if (!requestedServiceSlug) {
      return;
    }

    const matchedService = services.find(
      (service) =>
        service?.isActive !== false &&
        getServiceSlug(service, localizeText) === requestedServiceSlug,
    );

    setFormData((current) => {
      if (current.serviceType && current.message) {
        return current;
      }

      const inferredServiceType =
        current.serviceType ||
        resolveServiceTypeFromSlug(requestedServiceSlug, bookableAppointmentTypes);

      const serviceName = matchedService ? localizeText(matchedService.title) : requestedServiceSlug;
      const inferredMessage = current.message || `Interested in service: ${serviceName}`;

      return {
        ...current,
        serviceType: inferredServiceType,
        message: inferredMessage,
      };
    });
  }, [bookableAppointmentTypes, localizeText, location.search, services]);

  useEffect(() => {
    if (!formData.serviceType) {
      setPaymentQuote(null);
      setQuoteError('');
      setIsQuoteLoading(false);
      return;
    }

    let isCancelled = false;
    setIsQuoteLoading(true);

    fetchPaymentQuote(formData.serviceType)
      .then((quotePayload) => {
        if (isCancelled) {
          return;
        }

        setPaymentQuote(quotePayload);
        setQuoteError('');
        setFormData((current) => {
          const dueNowAmount =
            current.paymentMethod === 'paypal'
              ? resolveDueNowAmount(quotePayload, current.paymentOption)
              : quotePayload.baseAmount;
          const fullAmount = Number(quotePayload.totalAmount ?? 0);
          const normalizedDueNowAmount = Number(dueNowAmount ?? 0);

          return {
            ...current,
            paymentCurrency: normalizeCurrency(quotePayload.currency, defaultCurrency),
            baseAmount: quotePayload.baseAmount,
            taxPercent: quotePayload.taxPercent,
            taxAmount: quotePayload.taxAmount,
            bookingFeeAmount: quotePayload.bookingFeeAmount,
            minimumBookingPercent: quotePayload.minimumBookingPercent,
            minimumBookingAmount: quotePayload.minimumBookingAmount,
            totalAmount: quotePayload.totalAmount,
            paymentAmount: dueNowAmount,
            outstandingAmount:
              current.paymentMethod === 'paypal'
                ? Math.max(0, fullAmount - normalizedDueNowAmount)
                : quotePayload.baseAmount,
          };
        });
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        setPaymentQuote(null);
        setQuoteError(error?.message || 'Unable to load service pricing.');
        setFormData((current) => ({
          ...current,
          baseAmount: null,
          taxPercent: null,
          taxAmount: null,
          bookingFeeAmount: null,
          minimumBookingPercent: null,
          minimumBookingAmount: null,
          totalAmount: null,
          paymentAmount: null,
          outstandingAmount: null,
        }));
      })
      .finally(() => {
        if (!isCancelled) {
          setIsQuoteLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [defaultCurrency, formData.serviceType, formData.paymentMethod, formData.paymentOption]);

  useEffect(() => {
    if (checkoutStep !== 2 || formData.paymentMethod !== 'paypal') {
      setIsPayPalScriptReady(false);
      setPayPalError('');
      return;
    }

    if (!siteSettings.enablePaypalPayments) {
      setPayPalError('PayPal payments are currently disabled.');
      setIsPayPalScriptReady(false);
      return;
    }

    if (!siteSettings.paypalClientId) {
      setPayPalError('PayPal is not configured yet. Please contact support.');
      setIsPayPalScriptReady(false);
      return;
    }

    if (!paymentQuote || !paymentQuote.totalAmount || paymentQuote.totalAmount <= 0) {
      setPayPalError('Set a valid service price in admin before online checkout.');
      setIsPayPalScriptReady(false);
      return;
    }

    let isCancelled = false;

    loadPayPalSdk({
      clientId: siteSettings.paypalClientId,
      currency: normalizeCurrency(paymentQuote.currency || defaultCurrency, defaultCurrency),
    })
      .then(() => {
        if (!isCancelled) {
          setIsPayPalScriptReady(true);
          setPayPalError('');
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setPayPalError(error?.message || 'Unable to load PayPal checkout.');
          setIsPayPalScriptReady(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    checkoutStep,
    defaultCurrency,
    formData.paymentMethod,
    paymentQuote,
    siteSettings.enablePaypalPayments,
    siteSettings.paypalClientId,
  ]);

  useEffect(() => {
    if (!paymentQuote) {
      return;
    }

    setFormData((current) => {
      if (!current.serviceType) {
        return current;
      }

      if (current.paymentMethod === 'paypal') {
        const dueNowAmount = resolveDueNowAmount(paymentQuote, current.paymentOption);
        const outstandingAmount = Math.max(
          0,
          Number(paymentQuote.totalAmount || 0) - Number(dueNowAmount || 0),
        );

        if (
          current.paymentAmount === dueNowAmount &&
          current.outstandingAmount === outstandingAmount &&
          current.minimumBookingPercent === paymentQuote.minimumBookingPercent &&
          current.minimumBookingAmount === paymentQuote.minimumBookingAmount
        ) {
          return current;
        }

        return {
          ...current,
          paymentOption: normalizePaymentOption(current.paymentOption),
          paymentAmount: dueNowAmount,
          totalAmount: paymentQuote.totalAmount,
          minimumBookingPercent: paymentQuote.minimumBookingPercent,
          minimumBookingAmount: paymentQuote.minimumBookingAmount,
          outstandingAmount,
        };
      }

      const nextManualTotal = Number(paymentQuote.baseAmount || 0);
      if (
        current.paymentAmount === nextManualTotal &&
        current.totalAmount === nextManualTotal &&
        current.outstandingAmount === nextManualTotal
      ) {
        return current;
      }

      return {
        ...current,
        paymentOption: 'manual',
        paymentAmount: nextManualTotal,
        totalAmount: nextManualTotal,
        minimumBookingPercent: 0,
        minimumBookingAmount: 0,
        outstandingAmount: nextManualTotal,
      };
    });
  }, [formData.paymentMethod, formData.paymentOption, paymentQuote, formData.serviceType]);

  const validateRequiredFields = (currentFormData) => {
    if (
      !currentFormData.name ||
      !currentFormData.email ||
      !currentFormData.phone ||
      !currentFormData.serviceType
    ) {
      toast({
        title: tr('booking.toast.missingTitle'),
        description: tr('booking.toast.missingDescription'),
        variant: 'destructive',
      });
      return false;
    }

    if (!currentFormData.preferredDate || !currentFormData.preferredTime) {
      toast({
        title: tr('booking.toast.missingTitle'),
        description: tr(
          'booking.toast.missingDateTime',
          'Please choose a preferred date and hour before submitting.',
        ),
        variant: 'destructive',
      });
      return false;
    }

    const selectedDateKey = String(currentFormData.preferredDate || '').trim();
    const selectedHour = normalizePreferredHour(currentFormData.preferredTime);
    if (!selectedHour) {
      toast({
        title: tr('booking.toast.errorTitle'),
        description: tr(
          'booking.toast.invalidHour',
          'Preferred time must be selected in hourly format.',
        ),
        variant: 'destructive',
      });
      return false;
    }

    if (offDaysSet.has(selectedDateKey) || availability.offDay) {
      toast({
        title: tr('booking.toast.errorTitle'),
        description: tr(
          'booking.toast.offDay',
          'Selected day is blocked for bookings. Please choose another date.',
        ),
        variant: 'destructive',
      });
      return false;
    }

    if (selectedDateKey && selectableTimeSlots.length === 0) {
      toast({
        title: tr('booking.toast.errorTitle'),
        description: tr(
          'booking.toast.noSlots',
          'No available time slots for this date. Please choose another date.',
        ),
        variant: 'destructive',
      });
      return false;
    }

    if (selectedDateKey && !selectableTimeSlots.includes(selectedHour)) {
      toast({
        title: tr('booking.toast.errorTitle'),
        description: tr(
          'booking.toast.hourUnavailable',
          'Selected hour is no longer available. Please choose another time.',
        ),
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  const resetForm = () => {
    const preferredMethod = availablePaymentMethods[0] || 'manual';
    setFormData(
      createInitialFormData({
        paymentMethod: preferredMethod,
        paymentCurrency: defaultCurrency,
      }),
    );
    setPaymentQuote(null);
    setQuoteError('');
    setAvailability({
      availableTimeSlots: [],
      offDay: false,
      bufferMinutes: Number(siteSettings.bookingBufferMinutes || 0),
    });
    setAvailabilityError('');
    setIsAvailabilityLoading(false);
    setCheckoutStep(1);
    setConfirmationDetails(null);
    setPendingPayPalAppointment(null);
    setIsRetryingPayPalConfirmation(false);
    payPalOrderMetaRef.current = null;
  };

  const showConfirmationStep = (appointmentData = {}) => {
    const normalizedPreferredTime = normalizePreferredHour(appointmentData.preferredTime);
    setConfirmationDetails({
      appointmentId: String(appointmentData.id || '').trim(),
      serviceLabel: resolveServiceLabel(appointmentData.serviceType),
      preferredDate: String(appointmentData.preferredDate || '').trim(),
      preferredTime: normalizedPreferredTime || String(appointmentData.preferredTime || '').trim(),
      paymentMethod: String(appointmentData.paymentMethod || '').trim(),
      paymentStatus: String(appointmentData.paymentStatus || '').trim(),
      paymentAmount: Number(appointmentData.paymentAmount || 0),
      totalAmount: Number(appointmentData.totalAmount || 0),
      paymentCurrency: normalizeCurrency(appointmentData.paymentCurrency || defaultCurrency, defaultCurrency),
      name: String(appointmentData.name || '').trim(),
      email: String(appointmentData.email || '').trim(),
      paypalOrderId: String(appointmentData.paypalOrderId || '').trim(),
      paypalCaptureId: String(appointmentData.paypalCaptureId || '').trim(),
    });
    setPendingPayPalAppointment(null);
    setPayPalError('');
    setCheckoutStep(3);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!formData.serviceType || !formData.preferredDate) {
      setAvailability({
        availableTimeSlots: [],
        offDay: false,
        bufferMinutes: Number(siteSettings.bookingBufferMinutes || 0),
      });
      setAvailabilityError('');
      setIsAvailabilityLoading(false);
      return;
    }

    const normalizedDate = String(formData.preferredDate || '').trim();
    if (offDaysSet.has(normalizedDate)) {
      setAvailability({
        availableTimeSlots: [],
        offDay: true,
        bufferMinutes: Number(siteSettings.bookingBufferMinutes || 0),
      });
      setAvailabilityError('');
      setIsAvailabilityLoading(false);
      setFormData((current) => {
        if (!current.preferredTime) {
          return current;
        }
        return {
          ...current,
          preferredTime: '',
        };
      });
      return;
    }

    let isCancelled = false;
    setIsAvailabilityLoading(true);
    setAvailabilityError('');

    fetchServiceAvailability(formData.serviceType, normalizedDate)
      .then((payload) => {
        if (isCancelled) {
          return;
        }

        const normalizedSlots = normalizeTimeSlots(payload?.availableTimeSlots);
        const nextAvailability = {
          availableTimeSlots: normalizedSlots,
          offDay: Boolean(payload?.offDay),
          bufferMinutes: Math.max(0, Number(payload?.bufferMinutes || 0)),
        };
        setAvailability(nextAvailability);
        setFormData((current) => {
          const selectedHour = normalizePreferredHour(current.preferredTime);
          if (!selectedHour || normalizedSlots.includes(selectedHour)) {
            return current;
          }
          return {
            ...current,
            preferredTime: '',
          };
        });
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        setAvailability({
          availableTimeSlots: [],
          offDay: false,
          bufferMinutes: Number(siteSettings.bookingBufferMinutes || 0),
        });
        setAvailabilityError(error?.message || 'Failed to load available time slots.');
      })
      .finally(() => {
        if (!isCancelled) {
          setIsAvailabilityLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    formData.preferredDate,
    formData.serviceType,
    offDaysSet,
    siteSettings.bookingBufferMinutes,
  ]);

  useEffect(() => {
    if (
      checkoutStep !== 2 ||
      formData.paymentMethod !== 'paypal' ||
      !isPayPalScriptReady ||
      !window.paypal?.Buttons ||
      !payPalButtonRef.current
    ) {
      return;
    }

    payPalButtonRef.current.innerHTML = '';

    const buttons = window.paypal.Buttons({
      style: {
        layout: 'vertical',
        shape: 'rect',
        label: 'paypal',
      },
      createOrder: async () => {
        const currentFormData = formDataRef.current;

        if (!validateRequiredFields(currentFormData)) {
          throw new Error('Please complete required fields before paying.');
        }

        if (!paymentQuote || !paymentQuote.totalAmount || paymentQuote.totalAmount <= 0) {
          throw new Error('No valid payment quote is available for this service.');
        }

        const orderPayload = await createPaypalOrder({
          serviceType: currentFormData.serviceType,
          paymentOption: normalizePaymentOption(currentFormData.paymentOption),
          description: `Appointment: ${currentFormData.serviceType}`,
        });

        payPalOrderMetaRef.current = orderPayload;

        setFormData((current) => ({
          ...current,
          paypalOrderId: orderPayload.orderID,
          paymentStatus: 'pending',
          paymentOption: normalizePaymentOption(orderPayload.paymentOption || current.paymentOption),
          paymentCurrency: normalizeCurrency(orderPayload.currency, defaultCurrency),
          baseAmount: orderPayload.baseAmount,
          taxPercent: orderPayload.taxPercent,
          taxAmount: orderPayload.taxAmount,
          bookingFeeAmount: orderPayload.bookingFeeAmount,
          minimumBookingPercent: orderPayload.minimumBookingPercent,
          minimumBookingAmount: orderPayload.minimumBookingAmount,
          totalAmount: orderPayload.totalAmount,
          paymentAmount: orderPayload.dueNowAmount,
          outstandingAmount: Math.max(
            0,
            Number(orderPayload.totalAmount || 0) - Number(orderPayload.dueNowAmount || 0),
          ),
        }));

        return orderPayload.orderID;
      },
      onApprove: async (data) => {
        setIsPayPalProcessing(true);
        setIsSubmitting(true);
        setPendingPayPalAppointment(null);

        let captureResult = null;
        let appointmentPayload = null;
        try {
          captureResult = await capturePaypalOrder(data.orderID);
          const latestFormData = formDataRef.current;
          const latestQuote = payPalOrderMetaRef.current || paymentQuote;

          appointmentPayload = {
            ...latestFormData,
            paymentMethod: 'paypal',
            paymentOption: normalizePaymentOption(
              latestQuote?.paymentOption || latestFormData.paymentOption,
            ),
            paymentStatus:
              normalizePaymentOption(latestQuote?.paymentOption || latestFormData.paymentOption) === 'full'
                ? 'paid'
                : 'partially_paid',
            paymentAmount: latestQuote?.dueNowAmount ?? latestFormData.paymentAmount ?? null,
            paymentCurrency: normalizeCurrency(
              latestQuote?.currency || latestFormData.paymentCurrency,
              defaultCurrency,
            ),
            baseAmount: latestQuote?.baseAmount ?? null,
            taxPercent: latestQuote?.taxPercent ?? null,
            taxAmount: latestQuote?.taxAmount ?? null,
            bookingFeeAmount: latestQuote?.bookingFeeAmount ?? null,
            minimumBookingPercent: latestQuote?.minimumBookingPercent ?? null,
            minimumBookingAmount: latestQuote?.minimumBookingAmount ?? null,
            totalAmount: latestQuote?.totalAmount ?? null,
            outstandingAmount: Math.max(
              0,
              Number(latestQuote?.totalAmount ?? latestFormData.totalAmount ?? 0) -
                Number(latestQuote?.dueNowAmount ?? latestFormData.paymentAmount ?? 0),
            ),
            paypalOrderId: captureResult.orderID || data.orderID,
            paypalCaptureId: captureResult.captureID || '',
          };

          const savedAppointment = await submitAppointmentRequest(appointmentPayload);

          toast({
            title: tr('booking.toast.successTitle'),
            description: tr(
              'booking.payment.paypalSuccess',
              'Payment completed and appointment request submitted successfully.',
            ),
          });
          showConfirmationStep(savedAppointment);
        } catch (error) {
          if (captureResult && appointmentPayload) {
            setPendingPayPalAppointment(appointmentPayload);
            setPayPalError(
              tr(
                'booking.payment.captureNeedsConfirmation',
                'Payment was received on PayPal, but booking confirmation did not complete. Click "Confirm Booking" below to finish.',
              ),
            );
          }
          toast({
            title: tr('booking.toast.errorTitle'),
            description: error?.message || tr('booking.toast.errorDescription'),
            variant: 'destructive',
          });
        } finally {
          setIsPayPalProcessing(false);
          setIsSubmitting(false);
        }
      },
      onError: (error) => {
        setPayPalError(error?.message || 'PayPal payment failed.');
      },
    });

    if (typeof buttons.isEligible === 'function' && !buttons.isEligible()) {
      setPayPalError('PayPal is not available for this browser or location.');
      return;
    }

    buttons.render(payPalButtonRef.current).catch((error) => {
      setPayPalError(error?.message || 'Failed to render PayPal checkout buttons.');
    });

    return () => {
      if (payPalButtonRef.current) {
        payPalButtonRef.current.innerHTML = '';
      }
    };
  }, [
    checkoutStep,
    defaultCurrency,
    formData.paymentMethod,
    isPayPalScriptReady,
    paymentQuote,
    toast,
    tr,
  ]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      if (field === 'paymentMethod') {
        const nextMethod = value === 'paypal' ? 'paypal' : 'manual';
        return {
          ...prev,
          paymentMethod: nextMethod,
          paymentOption: nextMethod === 'paypal' ? normalizePaymentOption(prev.paymentOption) : 'manual',
          paymentStatus: nextMethod === 'paypal' ? 'pending' : 'pending_manual',
        };
      }

      if (field === 'paymentOption') {
        return {
          ...prev,
          paymentOption: normalizePaymentOption(value),
        };
      }

      if (field === 'preferredTime') {
        return {
          ...prev,
          preferredTime: normalizePreferredHour(value),
        };
      }

      if (field === 'serviceType') {
        return {
          ...prev,
          serviceType: value,
          preferredDate: '',
          preferredTime: '',
        };
      }

      if (field === 'preferredDate') {
        return {
          ...prev,
          preferredDate: value,
          preferredTime: '',
        };
      }

      return { ...prev, [field]: value };
    });
  };

  const goToPaymentStep = () => {
    if (!validateRequiredFields(formData)) {
      return;
    }
    setCheckoutStep(2);
    window.scrollTo(0, 0);
  };

  const goToServiceStep = () => {
    setCheckoutStep(1);
    window.scrollTo(0, 0);
  };

  const retryPayPalConfirmation = async () => {
    if (!pendingPayPalAppointment) {
      return;
    }

    setIsRetryingPayPalConfirmation(true);
    setIsSubmitting(true);

    try {
      const savedAppointment = await submitAppointmentRequest(pendingPayPalAppointment);
      toast({
        title: tr('booking.toast.successTitle'),
        description: tr(
          'booking.payment.confirmationRecovered',
          'Payment was already completed. Booking confirmation is now saved.',
        ),
      });
      showConfirmationStep(savedAppointment);
    } catch (error) {
      toast({
        title: tr('booking.toast.errorTitle'),
        description: error?.message || tr('booking.toast.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsRetryingPayPalConfirmation(false);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.paymentMethod === 'paypal') {
      toast({
        title: tr('booking.payment.paypalActionRequired', 'PayPal action required'),
        description: tr(
          'booking.payment.paypalUseButtons',
          'Use the PayPal buttons to complete payment and submit the booking.',
        ),
      });
      return;
    }

    if (!validateRequiredFields(formData)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        paymentMethod: 'manual',
        paymentOption: 'manual',
        paymentStatus: 'pending_manual',
        paymentAmount: paymentQuote?.baseAmount ?? null,
        paymentCurrency: normalizeCurrency(paymentQuote?.currency || defaultCurrency, defaultCurrency),
        baseAmount: paymentQuote?.baseAmount ?? null,
        taxPercent: 0,
        taxAmount: 0,
        bookingFeeAmount: 0,
        minimumBookingPercent: 0,
        minimumBookingAmount: 0,
        totalAmount: paymentQuote?.baseAmount ?? null,
        outstandingAmount: paymentQuote?.baseAmount ?? null,
      };

      const savedAppointment = await submitAppointmentRequest(payload);

      toast({
        title: tr('booking.toast.successTitle'),
        description: tr('booking.toast.successDescription'),
      });
      showConfirmationStep(savedAppointment);
    } catch (error) {
      toast({
        title: tr('booking.toast.errorTitle'),
        description: error?.message || tr('booking.toast.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-50">
      <section className="bg-gradient-to-br from-sage-600 to-earth-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Calendar className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-4">{text('bookingHeroTitle', tr('booking.hero.title'))}</h1>
          <p className="text-xl text-sage-100 max-w-2xl mx-auto">
            {text('bookingHeroSubtitle', tr('booking.hero.subtitle'))}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="border-sage-200 shadow-xl">
                <CardHeader className="bg-white border-b border-sage-200">
                  <CardTitle className="text-2xl text-sage-900">{text('bookingFormTitle', tr('booking.form.title'))}</CardTitle>
                  <CardDescription className="text-sage-600">
                    {text('bookingFormDescription', tr('booking.form.description'))}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 bg-white">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="rounded-lg border border-sage-200 bg-sage-50 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 font-medium ${
                            checkoutStep === 1
                              ? 'bg-sage-600 text-white'
                              : 'bg-sage-100 text-sage-700'
                          }`}
                        >
                          1. Service Selection
                        </span>
                        <span className="text-sage-400">→</span>
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 font-medium ${
                            checkoutStep === 2
                              ? 'bg-sage-600 text-white'
                              : 'bg-sage-100 text-sage-700'
                          }`}
                        >
                          2. Payment
                        </span>
                        <span className="text-sage-400">→</span>
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 font-medium ${
                            checkoutStep === 3
                              ? 'bg-sage-600 text-white'
                              : 'bg-sage-100 text-sage-700'
                          }`}
                        >
                          3. Confirmation
                        </span>
                      </div>
                    </div>

                    {checkoutStep === 1 && (
                      <>
                        <div className="space-y-4">
                          <h3 className="font-semibold text-sage-900 text-lg border-b border-sage-200 pb-2">{tr('booking.form.personalInfo')}</h3>

                          <div>
                            <Label htmlFor="name" className="text-sage-700">{tr('booking.form.fullName')}</Label>
                            <Input
                              id="name"
                              type="text"
                              placeholder={tr('booking.form.placeholder.fullName')}
                              value={formData.name}
                              onChange={(event) => handleChange('name', event.target.value)}
                              className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500"
                              required
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="email" className="text-sage-700">{tr('booking.form.email')}</Label>
                              <Input
                                id="email"
                                type="email"
                                placeholder={tr('booking.form.placeholder.email')}
                                value={formData.email}
                                onChange={(event) => handleChange('email', event.target.value)}
                                className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500"
                                required
                              />
                            </div>

                            <div>
                              <Label htmlFor="phone" className="text-sage-700">{tr('booking.form.phone')}</Label>
                              <Input
                                id="phone"
                                type="tel"
                                placeholder={tr('booking.form.placeholder.phone')}
                                value={formData.phone}
                                onChange={(event) => handleChange('phone', event.target.value)}
                                className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500"
                                required
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="font-semibold text-sage-900 text-lg border-b border-sage-200 pb-2">{tr('booking.form.serviceDetails')}</h3>

                          <div>
                            <Label htmlFor="serviceType" className="text-sage-700">{tr('booking.form.serviceType')}</Label>
                            <Select value={formData.serviceType} onValueChange={(value) => handleChange('serviceType', value)}>
                              <SelectTrigger className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500">
                                <SelectValue placeholder={tr('booking.form.placeholder.serviceType')} />
                              </SelectTrigger>
                              <SelectContent>
                                {bookableAppointmentTypes.map((type) => {
                                  const translatedLabel = appointmentTypeLabelsByValue[type.value] || localizeText(type.label);
                                  return (
                                    <SelectItem key={type.value} value={type.value}>
                                      {translatedLabel}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="preferredDate" className="text-sage-700">{tr('booking.form.preferredDate')}</Label>
                              <Input
                                id="preferredDate"
                                type="date"
                                value={formData.preferredDate}
                                onChange={(event) => handleChange('preferredDate', event.target.value)}
                                className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500"
                                min={new Date().toISOString().split('T')[0]}
                                required
                              />
                            </div>

                            <div>
                              <Label htmlFor="preferredTime" className="text-sage-700">{tr('booking.form.preferredTime')}</Label>
                              <select
                                id="preferredTime"
                                value={formData.preferredTime}
                                onChange={(event) => handleChange('preferredTime', event.target.value)}
                                className="mt-1.5 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                                required
                                disabled={
                                  !formData.serviceType ||
                                  !formData.preferredDate ||
                                  (formData.preferredDate &&
                                    selectableTimeSlots.length === 0 &&
                                    !isAvailabilityLoading)
                                }
                              >
                                <option value="">
                                  {tr('booking.form.placeholder.preferredTime', 'No specific hour')}
                                </option>
                                {selectableTimeSlots.map((slot) => (
                                  <option key={slot} value={slot}>
                                    {slot}
                                  </option>
                                ))}
                                {!formData.preferredDate &&
                                  configuredTimeSlots.map((item) => (
                                    <option key={item} value={item}>
                                      {item}
                                    </option>
                                  ))}
                              </select>
                              <p className="mt-1 text-xs text-sage-500">
                                {tr('booking.form.preferredTimeHint', 'Time is selected in hourly slots only.')}
                              </p>
                            </div>
                          </div>
                          {formData.serviceType && (
                            <div className="rounded-lg border border-sage-200 bg-sage-50 px-4 py-3 text-sm">
                              {isAvailabilityLoading && (
                                <p className="text-sage-700">
                                  {tr('booking.availability.loading', 'Checking available slots...')}
                                </p>
                              )}
                              {!isAvailabilityLoading && availabilityError && (
                                <p className="text-red-700">{availabilityError}</p>
                              )}
                              {!isAvailabilityLoading &&
                                !availabilityError &&
                                formData.preferredDate &&
                                (availability.offDay || offDaysSet.has(String(formData.preferredDate || '').trim())) && (
                                  <p className="text-red-700">
                                    {tr(
                                      'booking.availability.offDay',
                                      'This date is blocked as an off day. Please select another date.',
                                    )}
                                  </p>
                                )}
                              {!isAvailabilityLoading &&
                                !availabilityError &&
                                formData.preferredDate &&
                                !availability.offDay &&
                                !offDaysSet.has(String(formData.preferredDate || '').trim()) &&
                                selectableTimeSlots.length === 0 && (
                                  <p className="text-red-700">
                                    {tr(
                                      'booking.availability.none',
                                      'No available time slots remain for this date.',
                                    )}
                                  </p>
                                )}
                              {!isAvailabilityLoading &&
                                !availabilityError &&
                                formData.preferredDate &&
                                selectableTimeSlots.length > 0 && (
                                  <p className="text-sage-700">
                                    {tr(
                                      'booking.availability.count',
                                      `${selectableTimeSlots.length} time slots available for the selected date.`,
                                    )}
                                  </p>
                                )}
                              {selectedAppointmentType?.durationMinutes ? (
                                <p className="mt-1 text-xs text-sage-600">
                                  {tr(
                                    'booking.availability.duration',
                                    `Service duration: ${Math.max(15, Number(selectedAppointmentType.durationMinutes || 60))} minutes.`,
                                  )}
                                </p>
                              ) : null}
                              {Number(availability.bufferMinutes || siteSettings.bookingBufferMinutes || 0) > 0 ? (
                                <p className="mt-1 text-xs text-sage-600">
                                  {tr(
                                    'booking.availability.buffer',
                                    `A ${Math.max(
                                      0,
                                      Number(availability.bufferMinutes || siteSettings.bookingBufferMinutes || 0),
                                    )}-minute buffer is applied after confirmed bookings.`,
                                  )}
                                </p>
                              ) : null}
                            </div>
                          )}

                          <div>
                            <Label htmlFor="consultationType" className="text-sage-700">{tr('booking.form.consultationType')}</Label>
                            <Select value={formData.consultationType} onValueChange={(value) => handleChange('consultationType', value)}>
                              <SelectTrigger className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500">
                                <SelectValue placeholder={tr('booking.form.placeholder.consultationType')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="online">{tr('booking.form.consult.online')}</SelectItem>
                                <SelectItem value="in-person">{tr('booking.form.consult.inPerson')}</SelectItem>
                                <SelectItem value="either">{tr('booking.form.consult.either')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="font-semibold text-sage-900 text-lg border-b border-sage-200 pb-2">{tr('booking.form.additionalInfo')}</h3>

                          <div>
                            <Label htmlFor="message" className="text-sage-700">{tr('booking.form.messageOptional')}</Label>
                            <Textarea
                              id="message"
                              placeholder={tr('booking.form.placeholder.message')}
                              value={formData.message}
                              onChange={(event) => handleChange('message', event.target.value)}
                              className="mt-1.5 border-sage-300 focus:border-sage-500 focus:ring-sage-500 min-h-32"
                              rows={5}
                            />
                          </div>
                        </div>

                        <Button
                          type="button"
                          onClick={goToPaymentStep}
                          className="w-full bg-sage-600 hover:bg-sage-700 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-200 group"
                        >
                          Continue to Payment
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </>
                    )}

                    {checkoutStep === 2 && (
                      <>
                        <div className="rounded-lg border border-sage-200 bg-sage-50 p-4 text-sm text-sage-700 space-y-2">
                          <p className="font-semibold text-sage-900">Step 2: Payment</p>
                          <p>
                            <strong>Service:</strong> {selectedAppointmentLabel || '-'}
                          </p>
                          <p>
                            <strong>Date/Time:</strong> {formData.preferredDate || '-'}{' '}
                            {normalizePreferredHour(formData.preferredTime) || ''}
                          </p>
                          {formData.message ? (
                            <p className="text-xs text-sage-600">
                              <strong>Note:</strong> {formData.message}
                            </p>
                          ) : null}
                          <Button
                            type="button"
                            variant="outline"
                            className="border-sage-300"
                            onClick={goToServiceStep}
                          >
                            Back to Service Selection
                          </Button>
                        </div>

                        <div className="space-y-4">
                          <h3 className="font-semibold text-sage-900 text-lg border-b border-sage-200 pb-2">
                            {tr('booking.payment.title', 'Payment')}
                          </h3>

                          {availablePaymentMethods.length > 0 ? (
                            <>
                              <div>
                                <Label className="text-sage-700">{tr('booking.payment.method', 'Payment Method')}</Label>
                                <select
                                  value={formData.paymentMethod}
                                  onChange={(event) => handleChange('paymentMethod', event.target.value)}
                                  className="mt-1.5 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                                >
                                  {siteSettings.enableManualPayments && (
                                    <option value="manual">
                                      {tr('booking.payment.manual', 'Manual payment')}
                                    </option>
                                  )}
                                  {siteSettings.enablePaypalPayments && (
                                    <option value="paypal">
                                      {tr('booking.payment.paypal', 'PayPal')}
                                    </option>
                                  )}
                                </select>
                              </div>

                              {formData.paymentMethod === 'paypal' && (
                                <div>
                                  <Label className="text-sage-700">
                                    {tr('booking.payment.payNowOption', 'Pay Now Option')}
                                  </Label>
                                  <select
                                    value={normalizePaymentOption(formData.paymentOption)}
                                    onChange={(event) => handleChange('paymentOption', event.target.value)}
                                    className="mt-1.5 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                                  >
                                    <option value="minimum">
                                      {tr(
                                        'booking.payment.minimumOption',
                                        `Minimum required now (${Number(
                                          paymentQuote?.minimumBookingPercent ?? siteSettings.onlinePaymentMinimumPercent ?? 50,
                                        ).toFixed(0)}%)`,
                                      )}
                                    </option>
                                    <option value="full">
                                      {tr('booking.payment.fullOption', 'Pay full amount now (100%)')}
                                    </option>
                                  </select>
                                </div>
                              )}

                              <div className="rounded-lg border border-sage-200 bg-sage-50 p-4 text-sm text-sage-700">
                                <p className="font-semibold text-sage-900">
                                  {tr('booking.payment.cancellationTitle', 'Cancellation Policy')}
                                </p>
                                <p className="mt-1">
                                  {localizeText(siteSettings.cancellationTerms)}
                                </p>
                                <p className="mt-2 text-xs text-sage-600">
                                  {tr(
                                    'booking.payment.cancellationWindow',
                                    `Cancellation window: ${Math.max(
                                      1,
                                      Number(siteSettings.cancellationWindowHours || 24),
                                    )} hours before appointment. Late cancellations are non-refundable.`,
                                  )}
                                </p>
                              </div>

                              <div className="rounded-lg border border-sage-200 bg-sage-50 p-4 space-y-2 text-sm text-sage-700">
                                <p className="font-semibold text-sage-900">
                                  {tr('booking.payment.breakdown', 'Payment Breakdown')}
                                </p>

                                {isQuoteLoading && (
                                  <p>{tr('booking.payment.loadingQuote', 'Loading service quote...')}</p>
                                )}

                                {!isQuoteLoading && quoteError && (
                                  <p className="text-red-700">
                                    {quoteError}
                                  </p>
                                )}

                                {!isQuoteLoading && paymentQuote && (
                                  <div className="space-y-1">
                                    <p>
                                      <strong>{tr('booking.payment.basePrice', 'Base price')}:</strong>{' '}
                                      {formatMoney(paymentQuote.baseAmount, paymentQuote.currency)}
                                    </p>
                                    {formData.paymentMethod === 'paypal' && (
                                      <>
                                        <p>
                                          <strong>
                                            {tr('booking.payment.tax', 'Tax')} ({paymentQuote.taxPercent}%):
                                          </strong>{' '}
                                          {formatMoney(paymentQuote.taxAmount, paymentQuote.currency)}
                                        </p>
                                        <p>
                                          <strong>{tr('booking.payment.bookingFee', 'Online booking fee')}:</strong>{' '}
                                          {formatMoney(paymentQuote.bookingFeeAmount, paymentQuote.currency)}
                                        </p>
                                        <p>
                                          <strong>
                                            {tr('booking.payment.minimumDue', 'Minimum required now')} (
                                            {Number(paymentQuote.minimumBookingPercent || 50).toFixed(0)}%):
                                          </strong>{' '}
                                          {formatMoney(paymentQuote.minimumBookingAmount, paymentQuote.currency)}
                                        </p>
                                        <p>
                                          <strong>{tr('booking.payment.dueNow', 'Due now')}:</strong>{' '}
                                          {formatMoney(
                                            resolveDueNowAmount(paymentQuote, formData.paymentOption),
                                            paymentQuote.currency,
                                          )}
                                        </p>
                                        <p className="font-semibold text-sage-900">
                                          {tr('booking.payment.total', 'Total')}:{' '}
                                          {formatMoney(paymentQuote.totalAmount, paymentQuote.currency)}
                                        </p>
                                        <p>
                                          <strong>{tr('booking.payment.remaining', 'Remaining after booking')}:</strong>{' '}
                                          {formatMoney(
                                            Math.max(
                                              0,
                                              Number(paymentQuote.totalAmount || 0) -
                                                Number(resolveDueNowAmount(paymentQuote, formData.paymentOption) || 0),
                                            ),
                                            paymentQuote.currency,
                                          )}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>

                              {formData.paymentMethod === 'manual' && (
                                <div className="rounded-lg border border-sage-200 bg-sage-50 p-4 text-sm text-sage-700">
                                  <p className="font-medium text-sage-900 mb-1">
                                    {tr('booking.payment.manualInstructionsTitle', 'Manual Payment Instructions')}
                                  </p>
                                  <p>
                                    {localizeText(siteSettings.manualPaymentInstructions)}
                                  </p>
                                </div>
                              )}

                              {formData.paymentMethod === 'paypal' && (
                                <div className="space-y-3">
                                  <p className="text-sm text-sage-700">
                                    {tr(
                                      'booking.payment.paypalHint',
                                      'Complete PayPal checkout below. Your appointment request is submitted automatically after successful payment.',
                                    )}
                                  </p>

                                  {payPalError && (
                                    <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                                      {payPalError}
                                    </p>
                                  )}

                                  {isPayPalScriptReady ? (
                                    <div className="rounded-lg border border-sage-200 bg-white p-3">
                                      <div ref={payPalButtonRef} />
                                    </div>
                                  ) : (
                                    <div className="rounded-lg border border-sage-200 bg-sage-50 p-3 text-sm text-sage-600">
                                      {tr('booking.payment.loadingPaypal', 'Loading PayPal checkout...')}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                              {tr('booking.payment.noneEnabled', 'No payment methods are currently enabled.')}
                            </p>
                          )}
                        </div>

                        {formData.paymentMethod === 'manual' ? (
                          <Button
                            type="submit"
                            disabled={isSubmitting || !siteSettings.enableManualPayments}
                            className="w-full bg-sage-600 hover:bg-sage-700 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-200 group"
                          >
                            {isSubmitting ? (
                              tr('booking.form.submitting')
                            ) : (
                              <>
                                {tr('booking.form.submit')}
                                <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                              </>
                            )}
                          </Button>
                        ) : (
                          <>
                            <div className="rounded-lg border border-sage-200 bg-sage-50 p-3 text-sm text-sage-700">
                              {isPayPalProcessing
                                ? tr('booking.payment.processing', 'Processing PayPal payment...')
                                : tr('booking.payment.paypalWaiting', 'Use the PayPal buttons above to complete payment and submit your booking.')}
                            </div>
                            {pendingPayPalAppointment && (
                              <Button
                                type="button"
                                onClick={retryPayPalConfirmation}
                                disabled={isRetryingPayPalConfirmation || isSubmitting}
                                className="w-full bg-sage-600 hover:bg-sage-700 text-white"
                              >
                                {isRetryingPayPalConfirmation
                                  ? tr('booking.payment.confirmingBooking', 'Confirming booking...')
                                  : tr('booking.payment.confirmBookingNow', 'Confirm Booking')}
                              </Button>
                            )}
                          </>
                        )}

                        <p className="text-sm text-sage-600 text-center">
                          {tr('booking.form.privacy')}
                        </p>
                      </>
                    )}

                    {checkoutStep === 3 && confirmationDetails && (
                      <>
                        <div className="rounded-lg border border-green-200 bg-green-50 p-5 space-y-3">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="mt-0.5 h-6 w-6 text-green-700 flex-shrink-0" />
                            <div>
                              <p className="text-lg font-semibold text-green-900">Step 3: Confirmation</p>
                              <p className="text-sm text-green-800">
                                {tr(
                                  'booking.confirmation.saved',
                                  'Your booking request has been saved successfully.',
                                )}
                              </p>
                            </div>
                          </div>

                          <div className="rounded-md border border-green-200 bg-white p-4 text-sm text-sage-800 space-y-1">
                            <p>
                              <strong>{tr('booking.confirmation.reference', 'Booking Reference')}:</strong>{' '}
                              {confirmationDetails.appointmentId || '-'}
                            </p>
                            <p>
                              <strong>{tr('booking.confirmation.service', 'Service')}:</strong>{' '}
                              {confirmationDetails.serviceLabel || '-'}
                            </p>
                            <p>
                              <strong>{tr('booking.confirmation.dateTime', 'Date / Time')}:</strong>{' '}
                              {confirmationDetails.preferredDate || '-'} {confirmationDetails.preferredTime || ''}
                            </p>
                            <p>
                              <strong>{tr('booking.confirmation.client', 'Client')}:</strong>{' '}
                              {confirmationDetails.name || '-'} ({confirmationDetails.email || '-'})
                            </p>
                            <p>
                              <strong>{tr('booking.confirmation.paymentMethod', 'Payment Method')}:</strong>{' '}
                              {confirmationDetails.paymentMethod === 'paypal'
                                ? tr('booking.payment.paypal', 'PayPal')
                                : tr('booking.payment.manual', 'Manual payment')}
                            </p>
                            <p>
                              <strong>{tr('booking.confirmation.paymentStatus', 'Payment Status')}:</strong>{' '}
                              {String(confirmationDetails.paymentStatus || 'pending')
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                            </p>
                            <p>
                              <strong>{tr('booking.confirmation.paidNow', 'Paid / Due Now')}:</strong>{' '}
                              {formatMoney(
                                Number(confirmationDetails.paymentAmount || 0),
                                confirmationDetails.paymentCurrency,
                              )}
                            </p>
                            <p>
                              <strong>{tr('booking.confirmation.total', 'Total Service Amount')}:</strong>{' '}
                              {formatMoney(
                                Number(confirmationDetails.totalAmount || 0),
                                confirmationDetails.paymentCurrency,
                              )}
                            </p>
                            {confirmationDetails.paypalOrderId ? (
                              <p>
                                <strong>{tr('booking.confirmation.paypalOrder', 'PayPal Order ID')}:</strong>{' '}
                                {confirmationDetails.paypalOrderId}
                              </p>
                            ) : null}
                            {confirmationDetails.paypalCaptureId ? (
                              <p>
                                <strong>{tr('booking.confirmation.paypalCapture', 'PayPal Capture ID')}:</strong>{' '}
                                {confirmationDetails.paypalCaptureId}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <Button
                            type="button"
                            className="bg-sage-600 hover:bg-sage-700 text-white"
                            onClick={resetForm}
                          >
                            {tr('booking.confirmation.newBooking', 'Book Another Appointment')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="border-sage-300 text-sage-700"
                            onClick={() => {
                              window.location.href = '/';
                            }}
                          >
                            {tr('booking.confirmation.backHome', 'Back to Home')}
                          </Button>
                        </div>
                      </>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border-sage-200 shadow-lg bg-white">
                <CardHeader className="bg-sage-50 border-b border-sage-200">
                  <CardTitle className="text-lg text-sage-900">{tr('booking.nextSteps.title')}</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ol className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-sage-700 text-sm font-semibold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-sage-900">{tr('booking.nextSteps.step1Title')}</p>
                        <p className="text-sm text-sage-600">{tr('booking.nextSteps.step1Description')}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-sage-700 text-sm font-semibold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-sage-900">{tr('booking.nextSteps.step2Title')}</p>
                        <p className="text-sm text-sage-600">{tr('booking.nextSteps.step2Description')}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-sage-700 text-sm font-semibold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-sage-900">{tr('booking.nextSteps.step3Title')}</p>
                        <p className="text-sm text-sage-600">{tr('booking.nextSteps.step3Description')}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-sage-700 text-sm font-semibold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-sage-900">{tr('booking.nextSteps.step4Title')}</p>
                        <p className="text-sm text-sage-600">{tr('booking.nextSteps.step4Description')}</p>
                      </div>
                    </li>
                  </ol>
                </CardContent>
              </Card>

              <Card className="border-sage-200 shadow-lg bg-sage-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-sage-900 mb-3">{tr('booking.help.title')}</h3>
                  <p className="text-sm text-sage-700 mb-4">
                    {localizeText(siteSettings.bookingHelpText)}
                  </p>
                  <div className="space-y-2 text-sm text-sage-700">
                    <p>{tr('booking.help.phone')}: {siteSettings.contactPhone}</p>
                    <p>{tr('booking.help.email')}: {siteSettings.contactEmail}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
